import { useState, useEffect, useMemo } from "react";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  useCreateClinicMutation,
  type ClinicRequest,
} from "@/api/clinicApi";
import { useListTestsQuery } from "@/api/testApi";
import { useListVendorsQuery } from "@/api/vendorApi";
import { useListDrugPanelsQuery } from "@/features/drug-panels/services/drugPanelApi";
import ClinicTestsMultiSelect from "@/features/clinics/components/ClinicTestsMultiSelect";
import DrugPanelMultiSelect from "@/features/drug-panels/components/DrugPanelMultiSelect";
import {
  Building2,
  FlaskConical,
  AlertCircle,
  Clock,
  DollarSign
} from "lucide-react";
import { toast } from "sonner";

interface CreateClinicProps {
  vendorId?: number;
  onSuccess?: (newClinic: any) => void;
  onClose: () => void;
}

interface OperatingHours {
  mon: string;
  tue: string;
  wed: string;
  thu: string;
  fri: string;
  sat: string;
  sun: string;
  [key: string]: string;
}

export default function CreateClinic({
  vendorId: propVendorId,
  onSuccess,
  onClose,
}: CreateClinicProps) {
  const [activeTab, setActiveTab] = useState("general");
  const { data: allTestTypes = [] } = useListTestsQuery();
  const { data: vendors = [] } = useListVendorsQuery();
  const { data: drugPanels = [] } = useListDrugPanelsQuery({});

  // Filter out drug panels (stored as test types with category "Substance Abuse Testing")
  const availableTests = allTestTypes.filter(test => test.testCategory !== "Substance Abuse Testing");

  const [selectedVendorId, setSelectedVendorId] = useState<number | null>(
    propVendorId || null
  );
  const [vendorSearch, setVendorSearch] = useState("");
  const [vendorPopoverOpen, setVendorPopoverOpen] = useState(false);

  const filteredVendors = useMemo(() => {
    const query = vendorSearch.trim().toLowerCase();

    if (!query) return vendors;

    return vendors.filter((vendor: any) => {
      const haystack = [
        vendor.vendorName || "",
        vendor.contactEmail || "",
        vendor.contactPhone || "",
        vendor.city || "",
        vendor.state || "",
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [vendors, vendorSearch]);

  const [formData, setFormData] = useState<ClinicRequest>({
    clinicName: "",
    clinicAddress: "",
    city: "",
    state: "",
    zipCode: "",
    contactPhone: "",
    status: "ACTIVE",
    testTypeIds: [],
    operatingHours: undefined,
    collectionFee: undefined,
    drugPanelAvailability: undefined,
    clinicLevelPricing: undefined,
    appointmentRequired: false,
    cocRequired: false,
    resultDeliveryOverride: "DEFAULT",
  });

  const [selectedDrugPanelIds, setSelectedDrugPanelIds] = useState<number[]>([]);
  const [testPrices, setTestPrices] = useState<Record<number, string>>({});
  const [drugPrices, setDrugPrices] = useState<Record<number, string>>({});
  const [panelAliases, setPanelAliases] = useState<Record<number, string>>({});
  const [showOperatingHours, setShowOperatingHours] = useState(false);
  const [operatingHours, setOperatingHours] = useState<OperatingHours>({
    mon: "09:00-17:00",
    tue: "09:00-17:00",
    wed: "09:00-17:00",
    thu: "09:00-17:00",
    fri: "09:00-17:00",
    sat: "closed",
    sun: "closed",
  });

  const [hasCollectionFee, setHasCollectionFee] = useState<boolean>(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [createClinic, { isLoading }] = useCreateClinicMutation();

  const selectedVendor = vendors.find((v: any) => v.id === selectedVendorId);

  const isVariesByClinic = (value?: string) =>
    (value || "").toUpperCase() === "VARIES_BY_CLINIC";

  const resolveRequirementValue = (requirement?: string, currentValue?: boolean) => {
    const normalized = (requirement || "").toUpperCase();
    if (normalized === "REQUIRED_ALL") return true;
    if (normalized === "NOT_REQUIRED") return false;
    return currentValue ?? false;
  };

  useEffect(() => {
    if (selectedVendor) {
      if (selectedVendor.uniformOperatingHours) {
        setShowOperatingHours(false);
        try {
          const parsed = JSON.parse(selectedVendor.uniformOperatingHours);
          setOperatingHours(parsed);
        } catch (e) {
          // Silent fail
        }
      }

      if (selectedVendor.uniformTestConfiguration) {
        try {
          const parsedConfig = JSON.parse(selectedVendor.uniformTestConfiguration);
          let testIds: number[] = [];
          if (Array.isArray(parsedConfig)) {
            if (parsedConfig.length > 0 && typeof parsedConfig[0] === 'object') {
              testIds = parsedConfig.map((item: any) => item.testId);
            } else {
              testIds = parsedConfig;
            }
          }
          setFormData(prev => ({ ...prev, testTypeIds: testIds }));
        } catch (e) {
          // Silent fail
        }
      } else {
        setFormData(prev => ({ ...prev, testTypeIds: [] }));
      }

      if (selectedVendor.uniformDrugConfiguration) {
        try {
          const parsedDrugConfig = JSON.parse(selectedVendor.uniformDrugConfiguration);
          if (Array.isArray(parsedDrugConfig)) {
            const ids = parsedDrugConfig.map((item: any) => item.drugPanelId);
            setSelectedDrugPanelIds(ids);
          }
        } catch (e) {
          // Silent fail
        }
      } else {
        setSelectedDrugPanelIds([]);
      }

      setFormData((prev) => ({
        ...prev,
        appointmentRequired: resolveRequirementValue(
          selectedVendor.appointmentRequirement,
          prev.appointmentRequired
        ),
        cocRequired: resolveRequirementValue(
          selectedVendor.cocRequirement,
          prev.cocRequired
        ),
      }));
    }
  }, [selectedVendorId, selectedVendor]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleVendorChange = (value: string) => {
    const vendorId = value ? parseInt(value, 10) : null;
    setSelectedVendorId(vendorId);
    setVendorSearch("");
    setVendorPopoverOpen(false);
    if (errors.vendorId) {
      setErrors((prev) => ({ ...prev, vendorId: "" }));
    }
  };

  const handleTestTypesChange = (selectedIds: number[]) => {
    setFormData((prev) => ({ ...prev, testTypeIds: selectedIds }));
    const newPrices = { ...testPrices };
    Object.keys(newPrices).forEach(id => {
      if (!selectedIds.includes(parseInt(id))) delete newPrices[parseInt(id)];
    });
    setTestPrices(newPrices);

    if (errors.testTypeIds) {
      setErrors((prev) => ({ ...prev, testTypeIds: "" }));
    }
  };

  const handleDrugPanelsChange = (selectedIds: number[]) => {
    setSelectedDrugPanelIds(selectedIds);
    const newPrices = { ...drugPrices };
    Object.keys(newPrices).forEach(id => {
      if (!selectedIds.includes(parseInt(id))) delete newPrices[parseInt(id)];
    });
    setDrugPrices(newPrices);

    const newAliases = { ...panelAliases };
    Object.keys(newAliases).forEach(id => {
      if (!selectedIds.includes(parseInt(id))) delete newAliases[parseInt(id)];
    });
    setPanelAliases(newAliases);
  };

  const handlePanelAliasChange = (panelId: number, alias: string) => {
    setPanelAliases(prev => ({ ...prev, [panelId]: alias }));
  };

  const handleOperatingHoursChange = (day: keyof OperatingHours, value: string) => {
    setOperatingHours({ ...operatingHours, [day]: value });
  };

  const shouldShowOperationsTab = (): boolean => {
    // Show operations tab if vendor has location-specific collection fee configuration
    if (selectedVendor?.collectionFeeConfiguration) {
      try {
        const config = typeof selectedVendor.collectionFeeConfiguration === 'string'
          ? JSON.parse(selectedVendor.collectionFeeConfiguration)
          : selectedVendor.collectionFeeConfiguration;
        return config?.type === "LOCATION_SPECIFIC";
      } catch {
        return false;
      }
    }
    return false;
  };

  const handleTestPriceChange = (testId: number, price: string) => {
    setTestPrices({ ...testPrices, [testId]: price });
  };

  const handleDrugPriceChange = (panelId: number, price: string) => {
    setDrugPrices({ ...drugPrices, [panelId]: price });
  };


  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!selectedVendorId) {
      newErrors.vendorId = "Please select a vendor";
    }

    if (!formData.clinicName.trim()) {
      newErrors.clinicName = "Clinic name is required";
    }

    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData.state.trim()) {
      newErrors.state = "State is required";
    }

    if (!formData.contactPhone.trim()) {
      newErrors.contactPhone = "Contact phone is required";
    } else if (!/^\+?[0-9\s\-()]{10,}$/.test(formData.contactPhone)) {
      newErrors.contactPhone = "Invalid phone number format (minimum 10 digits)";
    }

    if (!formData.testTypeIds || formData.testTypeIds.length === 0) {
      newErrors.testTypeIds = "Please select at least one test type";
    }

    // Validate test prices when clinic-level pricing is required
    if (formData.testTypeIds && formData.testTypeIds.length > 0 && selectedVendor) {
      if (selectedVendor.pricingModel === "LOCATION_SPECIFIC") {
        // Clinic-level pricing is required - validate all test prices
        const missingTestPrices = formData.testTypeIds.filter(testId => {
          const price = testPrices[testId];
          return !price || price.trim() === "" || isNaN(parseFloat(price)) || parseFloat(price) <= 0;
        });

        if (missingTestPrices.length > 0) {
          newErrors.testPrices = `Please provide valid prices for all selected tests. Missing prices for ${missingTestPrices.length} test${missingTestPrices.length !== 1 ? 's' : ''}.`;
        }
      }
    }

    // Validate drug panel prices when clinic-level drug pricing is required
    if (selectedDrugPanelIds.length > 0 && selectedVendor) {
      if (!selectedVendor.uniformDrugConfiguration) {
        // Clinic-level drug pricing is required - validate all drug prices
        const missingDrugPrices = selectedDrugPanelIds.filter(panelId => {
          const price = drugPrices[panelId];
          return !price || price.trim() === "" || isNaN(parseFloat(price)) || parseFloat(price) <= 0;
        });

        if (missingDrugPrices.length > 0) {
          newErrors.drugPrices = `Please provide valid prices for all selected drug panels. Missing prices for ${missingDrugPrices.length} panel${missingDrugPrices.length !== 1 ? 's' : ''}.`;
        }
      }
    }


    // Validate result delivery override
    if (formData.resultDeliveryOverride && !["DEFAULT", "EMAIL", "FAX", "VENDOR_PORTAL"].includes(formData.resultDeliveryOverride)) {
      newErrors.resultDeliveryOverride = "Invalid result delivery method";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const result = await createClinic({
        vendorId: selectedVendorId!,
        data: {
          ...formData,
          resultDeliveryOverride: formData.resultDeliveryOverride === "DEFAULT" ? undefined : formData.resultDeliveryOverride,
          operatingHours: selectedVendor?.uniformOperatingHours 
              ? selectedVendor.uniformOperatingHours 
              : (showOperatingHours ? JSON.stringify(operatingHours) : undefined),
          drugPanelAvailability: selectedVendor?.uniformDrugConfiguration
              ? undefined
              : JSON.stringify(selectedDrugPanelIds),
          panelAliasMapping: selectedVendor?.uniformDrugConfiguration
              ? undefined
              : JSON.stringify(panelAliases),
          clinicLevelPricing: JSON.stringify({
            tests: selectedVendor?.pricingModel === "UNIFORM" ? undefined : testPrices,
            drugs: selectedVendor?.uniformDrugConfiguration ? undefined : drugPrices
          }),
        },
      }).unwrap();

      toast.success("Clinic created successfully");
      onSuccess?.(result);
      onClose();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to create clinic");
    }
  };

  return (
    <div className="flex flex-col w-full max-h-[78vh] overflow-hidden">
      <div className="p-4 border-b bg-muted/20">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full max-w-2xl mx-auto ${
            selectedVendor?.pricingModel === "LOCATION_SPECIFIC" || shouldShowOperationsTab()
              ? "grid-cols-4"
              : "grid-cols-2"
          }`}>
            <TabsTrigger value="general" className="gap-2">
              <Building2 className="h-4 w-4" /> <span className="hidden sm:inline">General</span>
            </TabsTrigger>
            <TabsTrigger value="services" className="gap-2">
              <FlaskConical className="h-4 w-4" /> <span className="hidden sm:inline">Services</span>
            </TabsTrigger>
            {selectedVendor?.pricingModel === "LOCATION_SPECIFIC" && (
              <TabsTrigger value="pricing" className="gap-2">
                <DollarSign className="h-4 w-4" /> <span className="hidden sm:inline">Pricing</span>
              </TabsTrigger>
            )}
            {shouldShowOperationsTab() && (
              <TabsTrigger value="operations" className="gap-2">
                <Clock className="h-4 w-4" /> <span className="hidden sm:inline">Operations</span>
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto overscroll-contain">
        <Tabs value={activeTab} className="w-full">
          {/* General Tab */}
          <TabsContent value="general" className="p-6 space-y-6 m-0 outline-none">
            <Card className="border-none shadow-none bg-transparent">
              <CardHeader className="px-0">
                <CardTitle className="text-xl flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" /> Basic Information
                </CardTitle>
                <CardDescription>Primary identification and contact details for the clinic.</CardDescription>
              </CardHeader>
              <CardContent className="px-0 space-y-4">
                {/* Vendor Selection */}
                <div>
                  <Label htmlFor="vendor">Vendor *</Label>
                  {propVendorId ? (
                    <div className="mt-1.5 p-3 bg-blue-50 border border-blue-200 rounded">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-blue-900">
                          {selectedVendor?.vendorName || "Loading vendor..."}
                        </span>
                        <span className="text-xs text-blue-600">
                          (Pre-selected)
                        </span>
                      </div>
                      {selectedVendor && (
                        <div className="mt-2 text-xs text-gray-600">
                          <p>Contact: {selectedVendor.contactEmail} | {selectedVendor.contactPhone}</p>
                          <p>Location: {selectedVendor.city}, {selectedVendor.state}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <Popover open={vendorPopoverOpen} onOpenChange={setVendorPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className="mt-1.5 w-full justify-between"
                            disabled={isLoading}
                          >
                            <span className="truncate">
                              {selectedVendor ? selectedVendor.vendorName : "Choose a Vendor"}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {selectedVendor ? "Change" : "Select"}
                            </span>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[420px] p-0" align="start">
                          <div className="p-2 border-b">
                            <Input
                              autoFocus
                              value={vendorSearch}
                              onChange={(e) => setVendorSearch(e.target.value)}
                              placeholder="Search vendor..."
                              className="h-9"
                            />
                          </div>
                          <div className="max-h-72 overflow-y-auto p-1">
                            {filteredVendors.length === 0 ? (
                              <div className="p-3 text-sm text-muted-foreground">No vendors found</div>
                            ) : (
                              filteredVendors.map((vendor: any) => (
                                <Button
                                  key={vendor.id}
                                  type="button"
                                  variant="ghost"
                                  className="w-full justify-start h-auto px-3 py-2 text-left"
                                  onClick={() => handleVendorChange(String(vendor.id))}
                                >
                                  <div className="flex flex-col items-start truncate">
                                    <span className="font-medium">{vendor.vendorName}</span>
                                    <span className="text-xs text-muted-foreground truncate">
                                      {vendor.contactEmail || vendor.contactPhone || "No contact"}
                                    </span>
                                  </div>
                                </Button>
                              ))
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                      {selectedVendor && (
                        <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs">
                          <p className="font-medium">Vendor Info:</p>
                          <p>Contact: {selectedVendor.contactEmail} | {selectedVendor.contactPhone}</p>
                          <p>Location: {selectedVendor.city}, {selectedVendor.state}</p>
                          <p className="mt-1">
                            Pricing Model: <span className="font-semibold">{selectedVendor.pricingModel || "LOCATION_SPECIFIC"}</span>
                          </p>
                        </div>
                      )}
                    </>
                  )}
                  {errors.vendorId && (
                    <p className="text-red-500 text-xs mt-1">{errors.vendorId}</p>
                  )}
                </div>

                {/* Vendor Configuration Summary */}
                {selectedVendor && (
                  <div className="p-3 bg-indigo-50 border border-indigo-200 rounded">
                    <h4 className="text-sm font-semibold text-indigo-900 mb-2">Vendor Configuration Summary</h4>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="font-medium">Pricing Model:</span>{" "}
                        <span className={selectedVendor.pricingModel === "UNIFORM" ? "text-orange-600 font-semibold" : ""}>
                          {selectedVendor.pricingModel || "LOCATION_SPECIFIC"}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Operating Hours:</span>{" "}
                        <span className={selectedVendor.uniformOperatingHours ? "text-orange-600 font-semibold" : "text-green-600"}>
                          {selectedVendor.uniformOperatingHours ? "UNIFORM" : "Location-Specific"}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Test Configuration:</span>{" "}
                        <span className={selectedVendor.uniformTestConfiguration ? "text-orange-600 font-semibold" : "text-green-600"}>
                          {selectedVendor.uniformTestConfiguration ? "UNIFORM" : "Location-Specific"}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Drug Configuration:</span>{" "}
                        <span className={selectedVendor.uniformDrugConfiguration ? "text-orange-600 font-semibold" : "text-green-600"}>
                          {selectedVendor.uniformDrugConfiguration ? "UNIFORM" : "Location-Specific"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="clinicName">Clinic Name *</Label>
                  <Input
                    id="clinicName"
                    name="clinicName"
                    placeholder="e.g., Apollo Clinic"
                    value={formData.clinicName}
                    onChange={handleChange}
                    disabled={isLoading}
                    required
                    className={`mt-1.5 ${errors.clinicName ? "border-red-500" : ""}`}
                  />
                  {errors.clinicName && (
                    <p className="text-red-500 text-xs mt-1">{errors.clinicName}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="clinicAddress">Clinic Address</Label>
                  <Input
                    id="clinicAddress"
                    name="clinicAddress"
                    placeholder="123 Main Street"
                    value={formData.clinicAddress}
                    onChange={handleChange}
                    disabled={isLoading}
                    className="mt-1.5"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="latitude">Latitude</Label>
                    <Input
                      id="latitude"
                      name="latitude"
                      type="number"
                      step="any"
                      placeholder="e.g. 40.7128"
                      value={formData.latitude || ""}
                      onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value ? parseFloat(e.target.value) : undefined }))}
                      disabled={isLoading}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="longitude">Longitude</Label>
                    <Input
                      id="longitude"
                      name="longitude"
                      type="number"
                      step="any"
                      placeholder="e.g. -74.0060"
                      value={formData.longitude || ""}
                      onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value ? parseFloat(e.target.value) : undefined }))}
                      disabled={isLoading}
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      placeholder="Mumbai"
                      value={formData.city}
                      onChange={handleChange}
                      disabled={isLoading}
                      required
                      className={`mt-1.5 ${errors.city ? "border-red-500" : ""}`}
                    />
                    {errors.city && (
                      <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      name="state"
                      placeholder="Maharashtra"
                      value={formData.state}
                      onChange={handleChange}
                      disabled={isLoading}
                      required
                      className={`mt-1.5 ${errors.state ? "border-red-500" : ""}`}
                    />
                    {errors.state && (
                      <p className="text-red-500 text-xs mt-1">{errors.state}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="zipCode">Zip Code</Label>
                    <Input
                      id="zipCode"
                      name="zipCode"
                      placeholder="400001"
                      value={formData.zipCode}
                      onChange={handleChange}
                      disabled={isLoading}
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="contactPhone">Contact Phone *</Label>
                  <Input
                    id="contactPhone"
                    name="contactPhone"
                    placeholder="+919876543210 or 9876543210"
                    value={formData.contactPhone}
                    onChange={handleChange}
                    disabled={isLoading}
                    required
                    className={`mt-1.5 ${errors.contactPhone ? "border-red-500" : ""}`}
                  />
                  {errors.contactPhone && (
                    <p className="text-red-500 text-xs mt-1">{errors.contactPhone}</p>
                  )}
                </div>

                <Separator className="my-6" />

                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(val) => setFormData(prev => ({ ...prev, status: val as any }))}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">Active</SelectItem>
                      <SelectItem value="INACTIVE">Inactive</SelectItem>
                      <SelectItem value="SUSPENDED">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services" className="p-6 space-y-6 m-0 outline-none">
            <Card className="border-none shadow-none bg-transparent">
              <CardHeader className="px-0">
                <CardTitle className="text-xl flex items-center gap-2">
                  <FlaskConical className="h-5 w-5 text-primary" /> Test Configuration
                </CardTitle>
                <CardDescription>Define which tests are available at this clinic location.</CardDescription>
              </CardHeader>
              <CardContent className="px-0 space-y-6">
                {selectedVendor && (
                  (isVariesByClinic(selectedVendor.appointmentRequirement) ||
                    isVariesByClinic(selectedVendor.cocRequirement)) && (
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded space-y-3">
                      <p className="text-xs text-slate-600">
                        Clinic-specific requirements (shown because vendor settings vary by clinic).
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {isVariesByClinic(selectedVendor.appointmentRequirement) && (
                          <div>
                            <Label>Appointment Requirement</Label>
                            <Select
                              value={formData.appointmentRequired ? "REQUIRED" : "WALK_INS"}
                              onValueChange={(value) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  appointmentRequired: value === "REQUIRED",
                                }))
                              }
                              disabled={isLoading}
                            >
                              <SelectTrigger className="mt-1.5">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="REQUIRED">Required</SelectItem>
                                <SelectItem value="WALK_INS">Not Required (Walk-ins)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {isVariesByClinic(selectedVendor.cocRequirement) && (
                          <div>
                            <Label>COC Requirement</Label>
                            <Select
                              value={formData.cocRequired ? "REQUIRED" : "NOT_REQUIRED"}
                              onValueChange={(value) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  cocRequired: value === "REQUIRED",
                                }))
                              }
                              disabled={isLoading}
                            >
                              <SelectTrigger className="mt-1.5">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="REQUIRED">Required</SelectItem>
                                <SelectItem value="NOT_REQUIRED">Not Required</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                )}

                {selectedVendor &&
                  (isVariesByClinic(selectedVendor.appointmentRequirement) ||
                    isVariesByClinic(selectedVendor.cocRequirement)) && (
                    <Separator className="my-6" />
                )}

                <div>
                  <Label>Available Tests *</Label>
                  <ClinicTestsMultiSelect
                    availableTests={availableTests}
                    selectedTestIds={formData.testTypeIds || []}
                    onChange={handleTestTypesChange}
                    disabled={isLoading || (!!selectedVendor?.uniformTestConfiguration)}
                  />
                  {selectedVendor?.uniformTestConfiguration && (
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                      <p className="font-medium text-yellow-800 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> Uniform Test Configuration
                      </p>
                      <p className="text-yellow-700">Test selection is uniform for this vendor and cannot be changed.</p>
                    </div>
                  )}
                  {errors.testTypeIds && (
                    <p className="text-red-500 text-xs mt-1">{errors.testTypeIds}</p>
                  )}
                </div>

                <Separator className="my-6" />

                <div>
                  <Label>Drug Panels Offered</Label>
                  <DrugPanelMultiSelect
                    availablePanels={drugPanels}
                    selectedPanelIds={selectedDrugPanelIds}
                    onChange={handleDrugPanelsChange}
                    disabled={isLoading || (!!selectedVendor?.uniformDrugConfiguration)}
                  />
                  {selectedVendor?.uniformDrugConfiguration && (
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                      <p className="font-medium text-yellow-800 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> Uniform Drug Configuration
                      </p>
                      <p className="text-yellow-700">Drug panels are uniform for this vendor and cannot be changed.</p>
                    </div>
                  )}
                </div>

                {!selectedVendor?.uniformDrugConfiguration && selectedDrugPanelIds.length > 0 && (
                  <div>
                    <Label className="block mb-2">Drug Panel Aliases</Label>
                    <div className="space-y-3 p-4 bg-muted/20 rounded-lg border border-dashed border-primary/20">
                      <p className="text-sm text-muted-foreground">
                        Set custom names for drug panels as they appear to this clinic. Leave empty to use the default panel name.
                      </p>
                      <div className="space-y-2">
                        {selectedDrugPanelIds.map(panelId => {
                          const panel = drugPanels.find(p => p.id === panelId);
                          return (
                            <div key={panelId} className="flex items-center gap-3 p-2 bg-background rounded border">
                              <div className="flex-1">
                                <div className="text-sm font-medium">{panel?.panelName || `Panel #${panelId}`}</div>
                                <div className="text-xs text-muted-foreground">{panel?.numComponents || 0} components</div>
                              </div>
                              <div className="flex-1">
                                <Input
                                  placeholder={`Custom name (e.g., "DOT 5-Panel")`}
                                  value={panelAliases[panelId] || ""}
                                  onChange={(e) => handlePanelAliasChange(panelId, e.target.value)}
                                  disabled={isLoading}
                                  className="h-8 text-sm"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing" className="p-6 space-y-6 m-0 outline-none">
            <Card className="border-none shadow-none bg-transparent">
              <CardHeader className="px-0">
                <CardTitle className="text-xl flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" /> Location Pricing
                </CardTitle>
                <CardDescription>Configure pricing for tests and drug panels at this clinic location.</CardDescription>
              </CardHeader>
              <CardContent className="px-0 space-y-6">
                {selectedVendor?.pricingModel === "LOCATION_SPECIFIC" && formData.testTypeIds && formData.testTypeIds.length > 0 && (
                  <div className="space-y-3">
                    <Label className="block font-semibold text-primary">Test Prices (Vendor Cost)</Label>
                    <div className="border rounded-xl overflow-hidden bg-background">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium">Test Name</th>
                            <th className="px-4 py-2 text-left w-40 font-medium">Cost ($)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {formData.testTypeIds.map(testId => {
                            const test = availableTests.find(t => t.id === testId);
                            return (
                              <tr key={testId} className="hover:bg-muted/50 transition-colors">
                                <td className="px-4 py-2">{test?.testName || `Test #${testId}`}</td>
                                <td className="px-4 py-2">
                                  <Input
                                    type="number"
                                    
                                    
                                    placeholder="0.00"
                                    value={testPrices[testId] || ""}
                                    onChange={(e) => handleTestPriceChange(testId, e.target.value)}
                                    className="h-8"
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {!selectedVendor?.uniformDrugConfiguration && selectedDrugPanelIds.length > 0 && (
                  <div className="space-y-3">
                    <Label className="block font-semibold text-primary">Drug Panel Prices (Vendor Cost)</Label>
                    <div className="border rounded-xl overflow-hidden bg-background">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium">Panel Name</th>
                            <th className="px-4 py-2 text-left w-40 font-medium">Cost ($)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {selectedDrugPanelIds.map(id => {
                            const panel = drugPanels.find(p => p.id === id);
                            return (
                              <tr key={id} className="hover:bg-muted/50 transition-colors">
                                <td className="px-4 py-2">{panel?.panelName || `Panel #${id}`}</td>
                                <td className="px-4 py-2">
                                  <Input
                                    type="number"
                                    
                                    
                                    placeholder="0.00"
                                    value={drugPrices[id] || ""}
                                    onChange={(e) => handleDrugPriceChange(id, e.target.value)}
                                    className="h-8"
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {selectedVendor?.pricingModel === "UNIFORM" && (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600">
                    ℹ️ Pricing is uniform for this vendor and managed at the vendor level.
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Operations Tab */}
          <TabsContent value="operations" className="p-6 space-y-6 m-0 outline-none">
            <Card className="border-none shadow-none bg-transparent">
              <CardHeader className="px-0">
                <CardTitle className="text-xl flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" /> Operating Hours
                </CardTitle>
                <CardDescription>Configure operating hours and collection fees for this clinic.</CardDescription>
              </CardHeader>
              <CardContent className="px-0 space-y-6">
                {selectedVendor?.uniformOperatingHours ? (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
                    <p className="font-medium text-yellow-800 flex items-center gap-1 mb-2">
                      <AlertCircle className="h-4 w-4" /> Uniform Operating Hours
                    </p>
                    <p className="text-yellow-700 mb-2">Operating hours are uniform for this vendor and cannot be customized.</p>
                    <div className="mt-2 text-xs text-gray-600 space-y-1">
                      {Object.entries(operatingHours).map(([day, hours]) => (
                        <p key={day}><span className="capitalize font-medium">{day}:</span> {hours}</p>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="setCustomHours"
                        checked={showOperatingHours}
                        onChange={(e) => setShowOperatingHours(e.target.checked)}
                        className="rounded border-gray-300"
                        disabled={isLoading}
                      />
                      <Label htmlFor="setCustomHours">Set Clinic-Specific Operating Hours</Label>
                    </div>

                    {showOperatingHours && (
                      <div className="grid grid-cols-2 gap-3 pl-6 border-l-2 border-blue-200">
                        {(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const).map((day) => (
                          <div key={day} className="flex items-center gap-2">
                            <Label className="w-12 capitalize text-sm font-medium">{day}:</Label>
                            <Input
                              type="text"
                              placeholder="09:00-17:00"
                              value={operatingHours[day] || ""}
                              onChange={(e) => handleOperatingHoursChange(day, e.target.value)}
                              disabled={isLoading}
                              className="flex-1 h-8 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                <Separator className="my-6" />

                {selectedVendor && (() => {
                  try {
                    const config = selectedVendor.collectionFeeConfiguration 
                      ? (typeof selectedVendor.collectionFeeConfiguration === 'string' 
                          ? JSON.parse(selectedVendor.collectionFeeConfiguration) 
                          : selectedVendor.collectionFeeConfiguration)
                      : null;
                      
                    if (config && config.type === "LOCATION_SPECIFIC") {
                      return (
                        <div className="space-y-4">
                           <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                              <div className="space-y-1">
                                <Label className="text-base font-semibold">Collection Fee Configuration</Label>
                                <p className="text-xs text-muted-foreground">Does this clinic apply collection fees for tests?</p>
                              </div>
                              <Select
                                value={hasCollectionFee ? "YES" : "NO"}
                                onValueChange={(val) => {
                                    setHasCollectionFee(val === "YES");
                                    if (val === "NO") {
                                        setFormData(prev => ({ ...prev, collectionFee: 0 }));
                                    }
                                }}
                              >
                                <SelectTrigger className="w-full sm:w-[180px]">
                                  <SelectValue placeholder="Has Fee?" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="YES">Yes (Applies Fees)</SelectItem>
                                  <SelectItem value="NO">No (Hide Fees)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                          {hasCollectionFee && (
                            <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                              <Label htmlFor="collectionFee">Collection Fee ($)</Label>
                              <Input
                                id="collectionFee"
                                type="number"
                                name="collectionFee"
                                placeholder="25.00"
                                
                                
                                value={formData.collectionFee || ""}
                                onChange={handleChange}
                                disabled={isLoading}
                                className="mt-1.5"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                This vendor allows location-specific collection fees.
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    } else if (config && config.type === "UNIFORM") {
                      return (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600">
                          ℹ️ Collection Fee is uniform for this vendor (${config.amount}).
                        </div>
                      );
                    } else if (config && config.type === "NONE") {
                        return (
                          <div className="p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600">
                            ℹ️ Collection Fees are disabled by the vendor.
                          </div>
                        );
                    }
                    return null;
                  } catch {
                    return null;
                  }
                })()}

                <Separator className="my-6" />

                {/* Result Delivery Section - Always shown */}
                <div className="space-y-3">
                  <Label className="block font-semibold text-primary">Result Delivery Override</Label>
                  <div className="space-y-2">
                    <Label htmlFor="resultDeliveryOverride">Delivery Method Override</Label>
                    <Select
                      value={formData.resultDeliveryOverride}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, resultDeliveryOverride: value }))}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Use vendor default" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DEFAULT">Use vendor default</SelectItem>
                        <SelectItem value="EMAIL">Email</SelectItem>
                        <SelectItem value="FAX">Fax</SelectItem>
                        <SelectItem value="VENDOR_PORTAL">Vendor Portal</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Override the vendor's default result delivery method for this clinic.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="sticky bottom-0 bg-background border-t p-4 flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={isLoading} type="button">
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Creating..." : "Create Clinic"}
          </Button>
        </div>
      </form>
    </div>
  );
}
